import React, { useEffect, useState } from "react";
import {
    Alert,
    Avatar,
    Button,
    Collapse,
    Divider,
    Form,
    Input,
    notification,
    Select,
    Tag,
    Upload,
    UploadFile,
} from "antd";
import { DownOutlined, LeftOutlined, UploadOutlined, UserOutlined } from "@ant-design/icons";
import type { CustomTagProps } from "rc-select/lib/BaseSelect";
import { GroupContext, PublicAssetsUrl, QueryKeys, SystemContext, UserContext } from "@Constants";
import AccountService from "@Services/AccountService";
import InspectorModuleTitle from "@Components/groups/InspectorModuleTitle";
import { IRolePayload } from "@Models/IRoleDetails";
import { getSystemId } from "@Utils";
import { useQueryClient } from "@tanstack/react-query";
import { ICheckCapFull } from "./types";
import appQueryClient from "@AppQueryClient";

interface INewUser {
    fullName: string;
    email: string;
    roles: string[];
}

enum EAccountCreationStatus {
    Idle,
    Creating,
    Success,
    Fail,
}

interface IProps {
    context: number;
    instance: string;
    checkCap: ICheckCapFull;
}

interface IUploadError {
    title: string;
    status: number;
    detail: string;
    errors: Record<string, string[]>;
}

const ModuleCreateUser: React.FC<IProps> = ({ context, instance, checkCap }: IProps) => {
    const [creationStatusMsg, setCreationStatusMsg] = useState<JSX.Element>(null);
    const [uploadStatusMsg, setUploadStatusMsg] = useState<JSX.Element>(null);
    const [creationStatus, setCreationStatus] = useState<EAccountCreationStatus>(EAccountCreationStatus.Idle);
    const [uploadStatus, setUploadStatus] = useState<EAccountCreationStatus>(EAccountCreationStatus.Idle);
    const [uploadErrorMsg, setUploadErrorMsg] = useState<string>("");
    const [fileList, setFileList] = useState<UploadFile[]>([{ uid: "-1", name: "xxx.csv", status: "done", url: "" }]);
    const { Panel } = Collapse;
    const roleOptions = [];
    const contexts = GroupContext || UserContext;
    const rolesQuery = appQueryClient.getQueryData<IRolePayload>([QueryKeys.Roles]);
    const availableRoles = rolesQuery.roles?.filter(
        (x) =>
            x.contextId === contexts ||
            (checkCap("Role/Assign", SystemContext, getSystemId()) && x.contextId === SystemContext)
    );
    const [creationForm] = Form.useForm();
    const [uploadForm] = Form.useForm();
    const queryClient = useQueryClient();

    const errorMessageMap = {
        "Error creating account from Auth API: Code:1010 Message:Create user failed. Detail: An account with the given email already exists.":
            "An account with the given email(s) already exists.",
        "No valid user items provided. Check the full names.": "Full name has not been provided.",
        "No valid user items provided. Check the role names.": "Role(s) provided is invalid or empty.",
        "No valid user items provided. Check the role names. Check the full names.":
            "Role(s) provided is invalid or empty and full name has not been provided.",
        "No valid user items provided.": "No valid users have been provided.",
        "CsvHelper.MissingFieldException": "Name, Email or Role Columns are missing or in the incorrect format.",
    };

    const handleUploadError = (error: string | IUploadError) => {
        if (typeof error === "string") {
            const errorMessage = errorMessageMap[error] || "";
            setUploadErrorMsg(errorMessage);
        } else if (typeof error === "object") {
            for (let key in error) {
                if (key.endsWith(".Email")) {
                    setUploadErrorMsg("Email is empty or invalid.");
                    break;
                }
            }
        } else {
            setUploadErrorMsg("");
        }
    };

    availableRoles?.forEach((role) => {
        roleOptions.push({
            value: role.roleId,
            label: role.name,
        });
    });

    const renderRoleTag = (props: CustomTagProps) => {
        const { label, closable, onClose } = props;
        const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
            event.preventDefault();
            event.stopPropagation();
        };
        return (
            <Tag
                color={"red"}
                onMouseDown={onPreventMouseDown}
                closable={closable}
                onClose={onClose}
                style={{ marginRight: 3 }}
            >
                {label}
            </Tag>
        );
    };

    const onSubmitCreate = async (values: INewUser) => {
        setCreationStatus(EAccountCreationStatus.Creating);
        const service = new AccountService();
        const result = await service.create(
            {
                fullName: values.fullName,
                email: values.email,
            },
            instance,
            values.roles
        );

        if (result.hasErrors) {
            return setCreationStatus(EAccountCreationStatus.Fail);
        }

        if (result.value.length > 0) {
            setCreationStatus(EAccountCreationStatus.Success);
        } else {
            setCreationStatus(EAccountCreationStatus.Fail);
        }
    };

    const onSubmitUpload = async (newFileList: UploadFile[]) => {
        if (newFileList.length === 0 || newFileList[newFileList.length - 1]?.originFileObj === undefined) return;
        const file = newFileList[newFileList.length - 1];
        if (file.originFileObj?.uid === fileList[0]?.uid) return;

        setFileList([file]);

        const formData = new FormData();
        formData.append("users", file.originFileObj);

        setUploadStatus(EAccountCreationStatus.Creating);
        const service = new AccountService();
        const result = await service.upload(instance, formData);

        if (result.hasErrors) {
            setUploadErrorMsg("");
            const error = result?.errors?.[0];
            if (error.hasOwnProperty("errors")) {
                if (Array.isArray(error["errors"])) {
                    handleUploadError(error["errors"][0]);
                } else {
                    handleUploadError(error["errors"]);
                }
            } else if (error.hasOwnProperty("title")) {
                handleUploadError(error["title"]);
            }
            return setUploadStatus(EAccountCreationStatus.Fail);
        }

        if (result.value.length > 0) {
            setUploadStatus(EAccountCreationStatus.Success);
        } else {
            setUploadStatus(EAccountCreationStatus.Fail);
        }
    };

    const invalidateQueries = async () => {
        if (Number(context) === SystemContext) return;
        await queryClient.invalidateQueries([QueryKeys.UserGroups]);
        await queryClient.invalidateQueries([QueryKeys.GroupMembers]);
        await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, instance]);
        await queryClient.invalidateQueries([QueryKeys.GroupUserAddresses, instance]);
        await queryClient.invalidateQueries([QueryKeys.GroupUserCredits, instance]);
    };

    // "normFile" fixing antd warning: [antd: Upload] `value` is not a valid prop, do you mean `fileList`?
    const normFile = (e: any) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e && e.fileList;
    };

    const onBeforeUpload = (file) => {
        const types = [
            "text/comma-separated-values",
            "text/csv",
            "application/vnd.ms-excel", // for csvs edited in excel
        ];
        const valid = types.includes(file.type);
        if (!valid) {
            notification.error({ message: "File type is invalid." });
        }
        return valid || Upload.LIST_IGNORE;
    };

    /**
     * Call the check capability methods passed from props with the appropriate context and instance
     * @param cap
     */
    const allowed = (cap: string) => {
        return checkCap(cap, context, instance);
    };

    useEffect(() => {
        switch (creationStatus) {
            case EAccountCreationStatus.Idle:
                setCreationStatusMsg(null);
                break;
            case EAccountCreationStatus.Creating:
                setCreationStatusMsg(<Alert message="Creating user. Please wait." showIcon type="info" />);
                break;
            case EAccountCreationStatus.Success:
                setCreationStatusMsg(<Alert message="User created successfully." showIcon type="success" />);
                creationForm.resetFields();
                invalidateQueries().then();
                break;
            case EAccountCreationStatus.Fail:
                setCreationStatusMsg(
                    <Alert message="Error. User with this email may already exist." showIcon type="error" />
                );
                break;
        }
    }, [creationStatus]);

    useEffect(() => {
        switch (uploadStatus) {
            case EAccountCreationStatus.Idle:
                setUploadStatusMsg(null);
                break;
            case EAccountCreationStatus.Creating:
                setUploadStatusMsg(<Alert message="Uploading users. Please wait." showIcon type="info" />);
                break;
            case EAccountCreationStatus.Success:
                setUploadStatusMsg(<Alert message="Users created successfully." showIcon type="success" />);
                uploadForm.resetFields();
                invalidateQueries().then();
                break;
            case EAccountCreationStatus.Fail:
                setUploadStatusMsg(
                    <Alert
                        message={`Errors creating users. ${uploadErrorMsg} Please check your .csv file and try again.`}
                        showIcon
                        type="error"
                    />
                );
                break;
        }
    }, [uploadStatus]);

    return (
        (allowed("Account/Create") || allowed("Account/Upload")) && (
            <>
                <Collapse
                    destroyInactivePanel={false}
                    bordered={false}
                    ghost
                    expandIconPosition={"end"}
                    expandIcon={({ isActive }) => (
                        <Avatar
                            size="small"
                            className={"collapse-icon"}
                            icon={isActive ? <DownOutlined /> : <LeftOutlined />}
                        />
                    )}
                >
                    <Panel
                        header={
                            <InspectorModuleTitle
                                title={"Create New User"}
                                slug={"create-user"}
                                fetching={creationStatus == EAccountCreationStatus.Creating}
                                icon={<UserOutlined />}
                            />
                        }
                        key={1}
                    >
                        {allowed("Account/Create") && (
                            <Form
                                form={creationForm}
                                name="create-user"
                                className="inspector-form"
                                onFinish={(values: INewUser) => {
                                    onSubmitCreate(values).then();
                                }}
                                autoComplete="off"
                            >
                                <Form.Item
                                    label="Full name"
                                    name="fullName"
                                    rules={[{ required: true, message: "Please enter new user's full name" }]}
                                >
                                    <Input
                                        onChange={() => setCreationStatus(EAccountCreationStatus.Idle)}
                                        name={"fullName"}
                                        disabled={creationStatus == EAccountCreationStatus.Creating}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="Email"
                                    name="email"
                                    rules={[
                                        {
                                            required: true,
                                            type: "email",
                                            message: "Please enter a valid email address",
                                        },
                                    ]}
                                >
                                    <Input
                                        onChange={() => setCreationStatus(EAccountCreationStatus.Idle)}
                                        type={"email"}
                                        name={"email"}
                                        disabled={creationStatus == EAccountCreationStatus.Creating}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Role"
                                    name="roles"
                                    rules={[{ required: true, message: "Please select at least one role" }]}
                                >
                                    <Select
                                        filterOption={true}
                                        optionFilterProp="label"
                                        onChange={() => setCreationStatus(EAccountCreationStatus.Idle)}
                                        disabled={creationStatus == EAccountCreationStatus.Creating}
                                        mode="multiple"
                                        showArrow
                                        tagRender={renderRoleTag}
                                        style={{ width: "100%" }}
                                        options={roleOptions}
                                    />
                                </Form.Item>

                                <Form.Item>
                                    <Button
                                        className="secondary"
                                        htmlType="submit"
                                        name="submit"
                                        disabled={creationStatus === EAccountCreationStatus.Creating}
                                        loading={creationStatus == EAccountCreationStatus.Creating}
                                    >
                                        Create
                                    </Button>
                                </Form.Item>
                                {creationStatusMsg}
                            </Form>
                        )}
                        {allowed("Account/Upload") && (
                            <Form form={uploadForm} name="upload-users" className="inspector-form" autoComplete="off">
                                <Divider />
                                <h4>Upload</h4>
                                <Alert
                                    style={{ marginBottom: "10px" }}
                                    description={
                                        <>
                                            You can also create new users from a .csv file containing Name, Email and
                                            Role columns.
                                            <a href={PublicAssetsUrl + "/web/upload_example.csv"}>
                                                <hr />
                                                <Button className="secondary">Download an example file</Button>
                                            </a>
                                        </>
                                    }
                                    type="info"
                                    showIcon
                                />
                                <Form.Item
                                    name="listUrl"
                                    valuePropName="fileList"
                                    getValueFromEvent={normFile}
                                    label="File"
                                >
                                    <Upload
                                        accept={"text/csv, text/comma-separated-values"}
                                        maxCount={1}
                                        className="upload-file"
                                        fileList={[...fileList]}
                                        beforeUpload={(file) => onBeforeUpload(file)}
                                        multiple={false}
                                        onChange={(val) => onSubmitUpload(val.fileList)}
                                        onRemove={() => setUploadStatus(EAccountCreationStatus.Idle)}
                                    >
                                        <Button
                                            className="secondary"
                                            disabled={uploadStatus === EAccountCreationStatus.Creating}
                                            icon={<UploadOutlined />}
                                            loading={uploadStatus == EAccountCreationStatus.Creating}
                                        >
                                            Upload .csv
                                        </Button>
                                    </Upload>
                                </Form.Item>
                                {EAccountCreationStatus.Fail || EAccountCreationStatus.Success ? uploadStatusMsg : null}
                            </Form>
                        )}
                    </Panel>
                </Collapse>
                <hr />
            </>
        )
    );
};
export default ModuleCreateUser;
